<template>
    <ul class="step-list no-bullet">
        <li
            v-for="i in stepLength"
            :key="i"
            class="step-wrapper"
            :class="{ 'step-completed-wrapper': currentStep > i - 1 }"
        >
            <div :class="getClasses(i)" :tabindex="i - 1 <= currentStep ? 0 : -1">
                <span class="visuallyhidden">
                    {{ t('step') }}
                    <span v-if="i - 1 < currentStep">{{ t('completed') }}</span>
                    <span v-if="i - 1 == currentStep">{{ t('current') }}</span>
                </span>
                <span v-if="i != stepLength">{{ i }}</span>
                <span v-else class="final-step">{{ t(finalStepLabel) }}</span>
            </div>
        </li>
    </ul>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

const props = defineProps({
    currentStep: {
        type: Number,
        default: 0,
    },
    stepLength: {
        type: Number,
        default: 3,
    },
    finalStepLabel: {
        type: String,
        default: 'yourFare',
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            step: 'Étape',
            completed: '(complétée)',
            current: '(en cours)',
            yourFare: 'Votre tarif',
            yourSituation: 'Votre situation',
            prev: 'Étape précédente',
            next: 'Étape suivante',
            newSearch: 'Nouvelle recherche',
        },
        en: {
            step: 'Step',
            completed: '(completed)',
            current: '(current)',
            yourFare: 'Your fare',
            yourSituation: 'Your situation',
            prev: 'Previous step',
            next: 'Next step',
            newSearch: 'New search',
        },
    },
});

function getClasses(i) {
    return {
        step: true,
        'step-active': props.currentStep === i - 1,
        'step-completed': props.currentStep > i - 1,
    };
}
</script>

<style lang="scss" scoped>
.step-wrapper {
    position: relative;
    flex-grow: 1;
    &.step-completed-wrapper {
        &::after {
            background-color: $color-blue-60;
        }
    }
    &::after {
        content: '';
        display: block;
        z-index: 0;
        position: absolute;
        top: 50%;
        width: 100%;
        height: 8px;
        background-color: $color-black-6;
        transform: translateY(-50%);
    }
    &:last-child {
        flex-grow: inherit;
        &::after {
            content: none;
            display: none;
        }
    }
}
.step-list {
    display: flex;
    justify-content: space-between;
    margin: 0 -4px 24px;
    @include breakpoint(medium) {
        margin: 0 -8px 32px;
    }
}
.step {
    @include rem-typo(16px);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    position: relative;
    box-sizing: content-box;
    min-width: 32px;
    height: 32px;
    text-align: center;
    color: $color-black;
    border: 4px solid $color-white;
    border-radius: 50px;
    background-color: $color-black-6;
    font-weight: 800;
    @media (max-width: 400px) {
        @include rem-typo(14px);
        border-width: 2px;
    }
    @include breakpoint(medium) {
        @include rem-typo(18px);
        border: 8px solid $color-white;
        min-width: 48px;
        height: 48px;
    }
}
.final-step {
    padding: 0 16px;
    @include breakpoint(medium) {
        padding: 0 20px;
    }
}
.step-completed {
    &::before {
        content: '';
        position: absolute;
        left: 50%;
        width: 32px;
        height: 32px;
        background-image: url('@@/img/blue-check.svg');
        background-size: cover;
        transform: translateX(-50%);
    }
}
.step-active,
.step-completed {
    color: $color-white;
    background-color: $color-blue-60;
    background-clip: content-box;
}
</style>
